import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import axios from 'axios';
import { Header } from '@/components/Header';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Download, Users, Mail, Globe, Loader2, ShieldAlert, BellRing, RefreshCw, Send, CheckCircle2, AlertCircle, Calendar } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SLUG = 'invest-uae-forum-2026';
const ADMIN_KEY_LS = 'skiaxis-admin-key-v1';
const LAST_SEEN_LS = 'skiaxis-admin-last-seen-v1';
const POLL_MS = 30000; // 30s auto-refresh

/**
 * Admin Events — view & export registrations for the Invest UAE Forum 2026.
 * Access controlled by an admin API key (one-time entry, kept in component state).
 */
export default function AdminEventsPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [adminKey, setAdminKey] = useState('');
  const [submittedKey, setSubmittedKey] = useState('');
  const [registrations, setRegistrations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [lastSeen, setLastSeen] = useState(() => localStorage.getItem(LAST_SEEN_LS) || '');
  const [lastRefresh, setLastRefresh] = useState(null);

  // View toggle: 'registrations' | 'speakers'
  const [view, setView] = useState('registrations');
  const [speakers, setSpeakers] = useState([]);
  const [speakersLoading, setSpeakersLoading] = useState(false);
  const [speakerEmails, setSpeakerEmails] = useState({}); // slug → email input
  const [sendingSlug, setSendingSlug] = useState(null);
  const [sendResults, setSendResults] = useState({}); // slug → { success, message, sent_at }

  // Soft gate: if the platform has an authenticated user, require admin role
  const isAdmin = !user || user.role === 'admin' || user.email === 'admin@skiaxis.com';

  const fetchRegs = async (key) => {
    setLoading(true);
    setError('');
    try {
      const { data } = await axios.get(`${API}/events/${SLUG}/registrations/list`, {
        params: { admin_key: key },
      });
      setRegistrations(data.registrations || []);
      setSubmittedKey(key);
      setLastRefresh(new Date());
      try { localStorage.setItem(ADMIN_KEY_LS, key); } catch {}
      // Also pre-fetch speakers for the share-kit tab
      fetchSpeakers(key);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Could not load registrations.');
      setRegistrations([]);
    } finally {
      setLoading(false);
    }
  };

  // Restore admin key from localStorage on mount → auto-authenticate
  useEffect(() => {
    if (!isAdmin) return;
    const stored = (() => { try { return localStorage.getItem(ADMIN_KEY_LS) || ''; } catch { return ''; } })();
    if (stored) {
      setAdminKey(stored);
      fetchRegs(stored);
      fetchSpeakers(stored);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin]);

  const fetchSpeakers = async (key) => {
    setSpeakersLoading(true);
    try {
      const { data } = await axios.get(`${API}/events/${SLUG}/admin/speakers`, { params: { admin_key: key } });
      setSpeakers(data.speakers || []);
    } catch (err) {
      // soft-fail — keep prior list
    } finally {
      setSpeakersLoading(false);
    }
  };

  const sendShareKit = async (speaker) => {
    const email = (speakerEmails[speaker.slug] || '').trim();
    if (!email || !speaker.slug) return;
    setSendingSlug(speaker.slug);
    setSendResults((p) => ({ ...p, [speaker.slug]: null }));
    try {
      const { data } = await axios.post(
        `${API}/events/${SLUG}/speakers/${speaker.slug}/send-share-kit`,
        { admin_key: submittedKey, recipient_email: email, recipient_name: speaker.name }
      );
      if (data?.success) {
        setSendResults((p) => ({ ...p, [speaker.slug]: { success: true, message: 'Share kit sent', sent_at: data.sent_at } }));
        // Refresh speaker list to update last_share_kit_sent_at
        fetchSpeakers(submittedKey);
      } else {
        setSendResults((p) => ({ ...p, [speaker.slug]: { success: false, message: data?.error || 'Send failed' } }));
      }
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.response?.data?.error || err?.message || 'Send failed';
      setSendResults((p) => ({ ...p, [speaker.slug]: { success: false, message: typeof msg === 'string' ? msg : 'Send failed' } }));
    } finally {
      setSendingSlug(null);
    }
  };

  // Auto-refresh every 30s once authenticated
  useEffect(() => {
    if (!submittedKey) return;
    const t = setInterval(() => fetchRegs(submittedKey), POLL_MS);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [submittedKey]);

  // Mark all current registrations as "seen" (clears the NEW badge counter)
  const markAllSeen = () => {
    const newest = registrations[0]?.created_at || new Date().toISOString();
    setLastSeen(newest);
    try { localStorage.setItem(LAST_SEEN_LS, newest); } catch {}
  };

  const newSinceSeen = useMemo(() => {
    if (!lastSeen) return registrations.length;
    return registrations.filter((r) => (r.created_at || '') > lastSeen).length;
  }, [registrations, lastSeen]);

  const isNewReg = (r) => {
    if (!r.created_at) return false;
    const created = new Date(r.created_at).getTime();
    return Date.now() - created < 24 * 60 * 60 * 1000;
  };

  const signOutAdmin = () => {
    try { localStorage.removeItem(ADMIN_KEY_LS); } catch {}
    setAdminKey('');
    setSubmittedKey('');
    setRegistrations([]);
  };

  const counts = useMemo(() => {
    const byRole = {};
    registrations.forEach((r) => {
      const k = r.join_as || 'attendee';
      byRole[k] = (byRole[k] || 0) + 1;
    });
    return byRole;
  }, [registrations]);

  const filtered = useMemo(() => {
    let list = registrations;
    if (filter !== 'all') list = list.filter((r) => (r.join_as || 'attendee') === filter);
    if (search.trim()) {
      const s = search.toLowerCase();
      list = list.filter((r) =>
        [r.full_name, r.email, r.country, r.organization, r.role]
          .filter(Boolean)
          .some((v) => v.toLowerCase().includes(s))
      );
    }
    return list;
  }, [registrations, filter, search]);

  const exportCSV = () => {
    if (filtered.length === 0) return;
    const headers = ['created_at', 'full_name', 'email', 'phone', 'country', 'role', 'organization', 'interest_track', 'join_as', 'message'];
    const escape = (v) => {
      if (v === null || v === undefined) return '';
      const s = String(v).replace(/"/g, '""');
      return /[",\n]/.test(s) ? `"${s}"` : s;
    };
    const rows = [headers.join(',')];
    filtered.forEach((r) => rows.push(headers.map((h) => escape(r[h])).join(',')));
    const blob = new Blob([rows.join('\n')], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `uae-forum-2026-registrations-${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-[#050505] text-white">
        <Header />
        <div className="max-w-3xl mx-auto px-6 py-24 text-center">
          <ShieldAlert className="h-12 w-12 mx-auto text-amber-500 mb-6" />
          <h1 className="text-3xl font-light mb-3">Admin Access Required</h1>
          <p className="text-white/60 mb-8">Sign in with an admin account to view event registrations.</p>
          <Button onClick={() => navigate('/')} data-testid="admin-back-home">Back to Home</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      <Header />
      <div className="max-w-7xl mx-auto px-6 py-12" data-testid="admin-events-page">
        <div className="flex items-start justify-between flex-wrap gap-4 mb-10">
          <div>
            <p className="text-xs uppercase tracking-[0.2em] text-amber-500 mb-2">Admin · Events</p>
            <h1 className="text-4xl font-light tracking-tight mb-2">Invest UAE Forum 2026</h1>
            <p className="text-white/50 text-sm">
              Live registrations for the 31 May 2026 forum.
              {lastRefresh && (
                <span className="text-white/30 ml-2">
                  · Last refresh {lastRefresh.toLocaleTimeString()} · auto-refresh every 30s
                </span>
              )}
            </p>
          </div>
          <div className="flex gap-2 items-center flex-wrap">
            {submittedKey && newSinceSeen > 0 && (
              <Badge
                className="bg-emerald-500/15 text-emerald-400 border border-emerald-500/30 px-3 py-1.5 text-xs uppercase tracking-wider flex items-center gap-2"
                data-testid="admin-new-since-seen"
              >
                <BellRing className="h-3.5 w-3.5" />
                {newSinceSeen} new since last visit
              </Badge>
            )}
            {submittedKey && newSinceSeen > 0 && (
              <Button variant="outline" size="sm" onClick={markAllSeen} data-testid="admin-mark-seen">
                Mark all seen
              </Button>
            )}
            {submittedKey && (
              <Button variant="outline" size="sm" onClick={() => fetchRegs(submittedKey)} disabled={loading} data-testid="admin-refresh">
                <RefreshCw className={`h-4 w-4 mr-1.5 ${loading ? 'animate-spin' : ''}`} />Refresh
              </Button>
            )}
            <Button variant="outline" onClick={() => navigate('/events/invest-uae-forum-2026')} data-testid="admin-view-public">View Public Page</Button>
            <Button onClick={exportCSV} disabled={filtered.length === 0} data-testid="admin-export-csv" className="bg-amber-500 text-black hover:bg-amber-400">
              <Download className="h-4 w-4 mr-2" />Export CSV ({filtered.length})
            </Button>
            {submittedKey && (
              <Button variant="ghost" size="sm" onClick={signOutAdmin} data-testid="admin-signout" className="text-white/40 hover:text-white">
                Sign out
              </Button>
            )}
          </div>
        </div>

        {/* Admin key gate */}
        {!submittedKey && (
          <Card className="bg-[#0A0A0A] border-white/10 p-8 max-w-xl mb-8">
            <h3 className="text-lg font-medium mb-2">Enter Admin API Key</h3>
            <p className="text-white/50 text-sm mb-4">Set in the backend's <code className="text-amber-500">ADMIN_API_KEY</code> environment variable.</p>
            <div className="flex gap-2">
              <Input
                type="password"
                value={adminKey}
                onChange={(e) => setAdminKey(e.target.value)}
                placeholder="Admin key"
                className="bg-[#050505] border-white/10"
                data-testid="admin-key-input"
                onKeyDown={(e) => { if (e.key === 'Enter' && adminKey.trim()) fetchRegs(adminKey.trim()); }}
              />
              <Button onClick={() => fetchRegs(adminKey.trim())} disabled={!adminKey.trim() || loading} data-testid="admin-key-submit">
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Authenticate'}
              </Button>
            </div>
            {error && <p className="text-red-400 text-sm mt-3" data-testid="admin-key-error">{error}</p>}
          </Card>
        )}

        {submittedKey && (
          <>
            {/* Tab toggle */}
            <div className="flex gap-1 mb-8 border-b border-white/10">
              <button
                onClick={() => setView('registrations')}
                className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${view === 'registrations' ? 'border-amber-500 text-amber-500' : 'border-transparent text-white/50 hover:text-white/80'}`}
                data-testid="admin-tab-registrations"
              >
                Registrations · <span className="text-white/40">{registrations.length}</span>
              </button>
              <button
                onClick={() => setView('speakers')}
                className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${view === 'speakers' ? 'border-amber-500 text-amber-500' : 'border-transparent text-white/50 hover:text-white/80'}`}
                data-testid="admin-tab-speakers"
              >
                Speaker Share Kit · <span className="text-white/40">{speakers.length}</span>
              </button>
            </div>

          {view === 'registrations' && (
          <>
            {/* Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
              <Card className="bg-[#0A0A0A] border-white/10 p-5">
                <div className="flex items-center gap-3 mb-2"><Users className="h-4 w-4 text-amber-500" /><span className="text-xs uppercase tracking-wider text-white/50">Total</span></div>
                <p className="text-3xl font-light">{registrations.length}</p>
              </Card>
              <Card className="bg-[#0A0A0A] border-white/10 p-5">
                <div className="flex items-center gap-3 mb-2"><Mail className="h-4 w-4 text-amber-500" /><span className="text-xs uppercase tracking-wider text-white/50">Attendees</span></div>
                <p className="text-3xl font-light">{counts.attendee || 0}</p>
              </Card>
              <Card className="bg-[#0A0A0A] border-white/10 p-5">
                <div className="flex items-center gap-3 mb-2"><Globe className="h-4 w-4 text-amber-500" /><span className="text-xs uppercase tracking-wider text-white/50">Investors</span></div>
                <p className="text-3xl font-light">{counts.investor || 0}</p>
              </Card>
              <Card className="bg-[#0A0A0A] border-white/10 p-5">
                <div className="flex items-center gap-3 mb-2"><Users className="h-4 w-4 text-amber-500" /><span className="text-xs uppercase tracking-wider text-white/50">Speakers / Panel</span></div>
                <p className="text-3xl font-light">{(counts.speaker || 0) + (counts.panelist || 0) + (counts.guest_expert || 0)}</p>
              </Card>
            </div>

            {/* Filters */}
            <div className="flex flex-wrap gap-3 mb-6 items-center">
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search name, email, org, country…"
                className="bg-[#0A0A0A] border-white/10 max-w-sm"
                data-testid="admin-search"
              />
              <select
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                className="bg-[#0A0A0A] border border-white/10 rounded-md px-3 py-2 text-sm"
                data-testid="admin-filter"
              >
                <option value="all">All roles</option>
                <option value="attendee">Attendees</option>
                <option value="investor">Investors</option>
                <option value="speaker">Speakers</option>
                <option value="panelist">Panelists</option>
                <option value="guest_expert">Guest Experts</option>
                <option value="startup">Startup Spotlights</option>
                <option value="sponsor">Sponsor Inquiries</option>
                <option value="partner">Institutional Partners</option>
              </select>
            </div>

            {/* Table */}
            <Card className="bg-[#0A0A0A] border-white/10 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="admin-registrations-table">
                  <thead className="border-b border-white/10 bg-[#080808]">
                    <tr className="text-left text-xs uppercase tracking-wider text-white/50">
                      <th className="px-4 py-3">Name</th>
                      <th className="px-4 py-3">Email</th>
                      <th className="px-4 py-3">Role</th>
                      <th className="px-4 py-3">Country</th>
                      <th className="px-4 py-3">Organization</th>
                      <th className="px-4 py-3">Joining As</th>
                      <th className="px-4 py-3">Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.length === 0 && (
                      <tr><td colSpan={7} className="px-4 py-12 text-center text-white/40">No registrations match the current filter.</td></tr>
                    )}
                    {filtered.map((r) => (
                      <tr key={r.id} className={`border-b border-white/5 hover:bg-white/[0.02] ${isNewReg(r) ? 'bg-emerald-500/[0.04]' : ''}`}>
                        <td className="px-4 py-3 font-medium">
                          <div className="flex items-center gap-2">
                            {r.full_name}
                            {isNewReg(r) && (
                              <span className="text-[9px] font-bold tracking-wider uppercase bg-emerald-500/20 text-emerald-400 border border-emerald-500/40 px-1.5 py-0.5 rounded" data-testid="admin-row-new-badge">NEW</span>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-white/70"><a href={`mailto:${r.email}`} className="hover:text-amber-500">{r.email}</a></td>
                        <td className="px-4 py-3 text-white/60">{r.role || '—'}</td>
                        <td className="px-4 py-3 text-white/60">{r.country || '—'}</td>
                        <td className="px-4 py-3 text-white/60">{r.organization || '—'}</td>
                        <td className="px-4 py-3"><Badge className="bg-amber-500/10 text-amber-500 border-amber-500/20">{r.join_as}</Badge></td>
                        <td className="px-4 py-3 text-white/40 text-xs">{r.created_at?.split('T')[0]}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          </>
          )}

          {view === 'speakers' && (
            <div className="space-y-4" data-testid="admin-speakers-panel">
              <Card className="bg-[#0A0A0A] border-white/10 p-5 mb-4">
                <div className="flex items-start gap-4">
                  <div className="flex-1">
                    <h3 className="text-base font-medium text-white mb-1">Speaker Share Kit</h3>
                    <p className="text-white/50 text-sm leading-relaxed">
                      Send each confirmed speaker a personalized email with their public profile URL, OG share card, pre-written LinkedIn/WhatsApp/X copy, and a calendar invite. Powered by Brevo. Each send is logged in MongoDB.
                    </p>
                  </div>
                  <Button variant="outline" size="sm" onClick={() => fetchSpeakers(submittedKey)} disabled={speakersLoading} data-testid="admin-speakers-refresh">
                    <RefreshCw className={`h-4 w-4 mr-1.5 ${speakersLoading ? 'animate-spin' : ''}`} />Refresh
                  </Button>
                </div>
              </Card>

              {speakers.length === 0 && !speakersLoading && (
                <Card className="bg-[#0A0A0A] border-white/10 p-8 text-center text-white/40">
                  No speakers loaded yet. Click Refresh.
                </Card>
              )}

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {speakers.map((sp, i) => {
                  const slugKey = sp.slug || `idx-${i}`;
                  const result = sendResults[slugKey];
                  const isSending = sendingSlug === slugKey;
                  const lastSent = sp.last_share_kit_sent_at ? new Date(sp.last_share_kit_sent_at).toLocaleString() : null;
                  const hasSlug = !!sp.slug;
                  return (
                    <Card key={slugKey} className="bg-[#0A0A0A] border-white/10 p-5" data-testid={`admin-speaker-card-${sp.slug || i}`}>
                      <div className="flex items-start gap-4 mb-4">
                        <div className="w-14 h-14 rounded-full bg-amber-500/10 border border-amber-500/30 flex items-center justify-center text-amber-500 font-medium text-sm flex-shrink-0">
                          {sp.initials || (sp.name || '').split(' ').map((p) => p[0]).slice(0, 2).join('')}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1 flex-wrap">
                            <h4 className="text-base font-medium text-white truncate">{sp.name}</h4>
                            {sp.keynote && <Badge className="bg-amber-500/15 text-amber-500 border-amber-500/30 text-[9px] tracking-wider uppercase">Keynote</Badge>}
                            {sp.confirmed && <Badge className="bg-emerald-500/15 text-emerald-400 border-emerald-500/30 text-[9px] tracking-wider uppercase">Confirmed</Badge>}
                          </div>
                          <p className="text-xs text-white/50 truncate">{sp.role}</p>
                          {sp.talk_title && <p className="text-xs text-white/40 mt-1 italic line-clamp-2">"{sp.talk_title}"</p>}
                        </div>
                      </div>

                      {!hasSlug && (
                        <div className="bg-amber-500/10 border border-amber-500/30 text-amber-400 text-xs p-2.5 rounded mb-3 flex items-start gap-2">
                          <AlertCircle className="h-3.5 w-3.5 flex-shrink-0 mt-0.5" />
                          <span>This speaker has no public profile slug yet — share kit unavailable. Add a <code>slug</code> in <code>events_routes.py</code> first.</span>
                        </div>
                      )}

                      {lastSent && (
                        <div className="flex items-center gap-2 text-xs text-white/50 mb-3">
                          <Calendar className="h-3 w-3 text-amber-500" />
                          <span>Last sent {lastSent}</span>
                          {sp.last_share_kit_recipient && <span className="text-white/30">→ {sp.last_share_kit_recipient}</span>}
                        </div>
                      )}

                      <div className="flex gap-2">
                        <Input
                          type="email"
                          value={speakerEmails[slugKey] || ''}
                          onChange={(e) => setSpeakerEmails((p) => ({ ...p, [slugKey]: e.target.value }))}
                          placeholder="speaker@example.com"
                          className="bg-[#050505] border-white/10 text-sm h-9"
                          disabled={!hasSlug || isSending}
                          data-testid={`admin-speaker-email-${sp.slug || i}`}
                          onKeyDown={(e) => { if (e.key === 'Enter') sendShareKit(sp); }}
                        />
                        <Button
                          size="sm"
                          onClick={() => sendShareKit(sp)}
                          disabled={!hasSlug || !((speakerEmails[slugKey] || '').includes('@')) || isSending}
                          className="bg-amber-500 text-black hover:bg-amber-400 h-9"
                          data-testid={`admin-speaker-send-${sp.slug || i}`}
                        >
                          {isSending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><Send className="h-3.5 w-3.5 mr-1.5" />Send</>}
                        </Button>
                      </div>

                      {result && (
                        <div
                          className={`mt-3 text-xs flex items-start gap-2 p-2.5 rounded ${result.success ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/30' : 'bg-red-500/10 text-red-400 border border-red-500/30'}`}
                          data-testid={`admin-speaker-result-${sp.slug || i}`}
                        >
                          {result.success ? <CheckCircle2 className="h-3.5 w-3.5 flex-shrink-0 mt-0.5" /> : <AlertCircle className="h-3.5 w-3.5 flex-shrink-0 mt-0.5" />}
                          <span>{result.message}</span>
                        </div>
                      )}

                      {sp.slug && (
                        <div className="mt-3 pt-3 border-t border-white/5 flex items-center justify-between text-[10px] text-white/40 uppercase tracking-wider">
                          <a href={`/forum/speakers/${sp.slug}`} target="_blank" rel="noopener noreferrer" className="hover:text-amber-500">View profile →</a>
                          {sp.calendly_url && <a href={sp.calendly_url} target="_blank" rel="noopener noreferrer" className="hover:text-amber-500">Calendly →</a>}
                        </div>
                      )}
                    </Card>
                  );
                })}
              </div>
            </div>
          )}
          </>
        )}
      </div>
    </div>
  );
}
